import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import "./FAQ.scss";
import FaqAnswerContent from "./FaqAnswerContent";
import FaqAskModal from "./FaqAskModal";
import FaqAssistUnavailableModal from "./FaqAssistUnavailableModal";
import FaqAssistMark, { FaqAssistAiIcon } from "./FaqAssistMark";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildFaqPageTourSteps } from "./faqPageTourSteps";
import {
  hasCompletedFaqPageTour,
  markFaqPageTourCompleted,
} from "./faqPageTourStorage";
import {
  fetchPlatformFaqs,
  fetchPlatformUserGuideFile,
  fetchPlatformUserGuides,
  fetchFaqAiStatus,
} from "../../../services/faqService";
import { submitFeedbackToClickUp } from "../../../services/clickupService";
import { useLoader } from "../../../context/LoaderContext";
import ToastContainer from "../../common/ToastContainer";
import useToast from "../../../hooks/useToast";
import { useUserData } from "../../../hooks/useUserData";
import { organizationCanSeeFaqTopic } from "../../../utils/orgModuleAccess";
import {
  buildFaqRecentId,
  pushFaqRecent,
  readFaqRecent,
  subscribeFaqRecent,
} from "./faqRecentActivity";

const KEYWORD_LIMIT = 20;
const SUGGESTION_DELAY = 200;

/** Match main sidebar module icons where possible. */
const FAQ_MODULE_ICONS = {
  QMS: "fas fa-file-alt",
  CAPA: "fas fa-clipboard-check",
  eISF: "fas fa-folder",
  Admin: "fas fa-wrench",
  CTMS: "fas fa-calendar-check",
  "My Inbox": "fas fa-bell",
  "Login & Access": "fas fa-key",
  "Study Management": "fas fa-gear",
  "User Guide": "fas fa-book",
};

/** Sidebar module accent keys — unknown modules keep the default slate. */
const FAQ_MODULE_ACCENTS = {
  QMS: "qms",
  CAPA: "capa",
  eISF: "eisf",
  Admin: "admin",
  CTMS: "ctms",
  "My Inbox": "inbox",
  "Login & Access": "login",
  "Study Management": "study",
  "Platform Navigation": "platform",
  "User Guide": "guide",
};

function faqModuleIcon(moduleName) {
  const key = String(moduleName || "").trim();
  if (FAQ_MODULE_ICONS[key]) return FAQ_MODULE_ICONS[key];
  const lower = key.toLowerCase();
  if (lower.includes("qms")) return FAQ_MODULE_ICONS.QMS;
  if (lower.includes("capa")) return FAQ_MODULE_ICONS.CAPA;
  if (lower.includes("eisf") || lower.includes("isf")) return FAQ_MODULE_ICONS.eISF;
  if (lower.includes("admin")) return FAQ_MODULE_ICONS.Admin;
  if (lower.includes("ctms") || lower.includes("eplanner") || lower.includes("e-planner"))
    return FAQ_MODULE_ICONS.CTMS;
  if (lower.includes("inbox") || lower.includes("notification"))
    return FAQ_MODULE_ICONS["My Inbox"];
  if (lower.includes("study")) return FAQ_MODULE_ICONS["Study Management"];
  if (lower.includes("login") || lower.includes("access"))
    return FAQ_MODULE_ICONS["Login & Access"];
  return "fas fa-folder";
}

function faqModuleAccent(moduleName) {
  const key = String(moduleName || "").trim();
  if (FAQ_MODULE_ACCENTS[key]) return FAQ_MODULE_ACCENTS[key];
  const lower = key.toLowerCase();
  if (lower.includes("qms")) return "qms";
  if (lower.includes("capa")) return "capa";
  if (lower.includes("eisf") || lower.includes("isf")) return "eisf";
  if (lower.includes("admin")) return "admin";
  if (lower.includes("ctms") || lower.includes("eplanner") || lower.includes("e-planner"))
    return "ctms";
  if (lower.includes("inbox") || lower.includes("notification")) return "inbox";
  if (lower.includes("study")) return "study";
  if (lower.includes("platform") && lower.includes("navigation")) return "platform";
  if (lower.includes("login") || lower.includes("access")) return "login";
  if (lower.includes("guide")) return "guide";
  return "default";
}

function faqUsesAqLogo(moduleName) {
  return faqModuleAccent(moduleName) === "platform";
}

function faqDomKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "");
}

function faqTreeModId(mainModule) {
  return `faq-tree-mod-${faqDomKey(mainModule)}`;
}

function faqTreeLeafId(mainModule, subModule) {
  return `faq-tree-leaf-${faqDomKey(mainModule)}-${faqDomKey(subModule)}`;
}

/** Scroll a target into view inside a scrollable ancestor (tree / questions list). */
function scrollWithinContainer(el, containerSelector) {
  if (!el) return;
  const list = el.closest(containerSelector);
  if (!list) {
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    return;
  }
  const listRect = list.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const nextTop = list.scrollTop + (elRect.top - listRect.top) - 12;
  list.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
}

const FAQ = () => {
  const { showLoader, hideLoader } = useLoader();
  const { toasts, showToast, removeToast } = useToast();
  const { userData } = useUserData();

  const [faqData, setFaqData] = useState({});
  const [loadError, setLoadError] = useState("");

  const [expandedItems, setExpandedItems] = useState(() => new Set());
  const [activeMainModule, setActiveMainModule] = useState("");
  const [activeSubModule, setActiveSubModule] = useState("");
  const [expandedNav, setExpandedNav] = useState(() => new Set());
  const [expandedUserGuide, setExpandedUserGuide] = useState(false);
  const [userGuides, setUserGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [askModalOpen, setAskModalOpen] = useState(false);
  const [askLaunchSession, setAskLaunchSession] = useState(null);
  const [assistUnavailableOpen, setAssistUnavailableOpen] = useState(false);
  const [assistUnavailableMessage, setAssistUnavailableMessage] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [recentItems, setRecentItems] = useState(() => readFaqRecent());
  const [pendingExpandFaqId, setPendingExpandFaqId] = useState("");
  const [pendingTreeScroll, setPendingTreeScroll] = useState(null);
  const [showFaqTour, setShowFaqTour] = useState(false);

  const visibleFaqData = useMemo(() => {
    const out = {};
    Object.entries(faqData || {}).forEach(([main, subs]) => {
      if (organizationCanSeeFaqTopic(userData, main)) {
        out[main] = subs;
      }
    });
    return out;
  }, [faqData, userData]);

  const visibleUserGuides = useMemo(
    () =>
      (userGuides || []).filter((guide) =>
        organizationCanSeeFaqTopic(
          userData,
          guide?.moduleName || guide?.moduleKey,
        ),
      ),
    [userGuides, userData],
  );

  const applyNestedFaqData = useCallback((nested) => {
    const data = nested && typeof nested === "object" ? nested : {};
    setFaqData(data);
    const mains = Object.keys(data);
    if (mains.length === 0) {
      setActiveMainModule("");
      setActiveSubModule("");
      setExpandedNav(new Set());
      return;
    }
    // Keep selection only if it still exists; never auto-expand a branch on load.
    setActiveMainModule((prev) => (prev && data[prev] ? prev : ""));
    setExpandedNav((prev) => new Set([...prev].filter((id) => data[id])));
  }, []);

  const loadFaqs = useCallback(async () => {
    showLoader();
    setLoadError("");
    try {
      const { nested } = await fetchPlatformFaqs();
      applyNestedFaqData(nested && Object.keys(nested).length > 0 ? nested : {});
    } catch (err) {
      applyNestedFaqData({});
      setLoadError(err?.message || "Could not load FAQs.");
    } finally {
      hideLoader();
    }
  }, [applyNestedFaqData, showLoader, hideLoader]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const loadUserGuides = useCallback(async () => {
    try {
      const { guides } = await fetchPlatformUserGuides();
      setUserGuides(guides || []);
    } catch {
      setUserGuides([]);
    }
  }, []);

  useEffect(() => {
    loadUserGuides();
  }, [loadUserGuides]);

  const openUserGuide = useCallback(
    async (guide) => {
      const moduleKey = guide?.moduleKey || guide?.moduleName;
      if (!moduleKey || guideLoading) return;
      const moduleName = guide?.moduleName || moduleKey;
      setGuideLoading(true);
      setExpandedUserGuide(true);
      setSearchQuery("");
      setShowSuggestions(false);
      setActiveMainModule("");
      setActiveSubModule("");
      setSelectedGuide({
        moduleKey,
        moduleName,
        fileUrl: String(guide?.fileUrl || "").trim(),
        fileName: guide?.fileName || "Guide",
      });
      try {
        let fileUrl = String(guide?.fileUrl || "").trim();
        let resolvedName = moduleName;
        let fileName = guide?.fileName || "Guide";

        if (!fileUrl) {
          const file = await fetchPlatformUserGuideFile(moduleKey);
          fileUrl = String(file?.fileUrl || "").trim();
          resolvedName = file.moduleName || resolvedName;
          fileName = file.fileName || fileName;
        }

        if (!fileUrl) {
          throw new Error("This user guide has no document link to display.");
        }

        setSelectedGuide({
          moduleKey,
          moduleName: resolvedName,
          fileUrl,
          fileName,
        });
      } catch (err) {
        setSelectedGuide(null);
        setLoadError(err?.message || "Could not load the user guide.");
      } finally {
        setGuideLoading(false);
      }
    },
    [guideLoading],
  );

  const closeUserGuide = useCallback(() => {
    setSelectedGuide(null);
  }, []);

  const selectFaqTopic = useCallback(
    (mainId, subId = "") => {
      setSelectedGuide(null);
      setActiveMainModule(mainId);
      if (subId) {
        setActiveSubModule(subId);
        setExpandedNav((prev) => new Set(prev).add(mainId));
        return;
      }
      const subModules = Object.keys(visibleFaqData[mainId] || {});
      setExpandedNav((prev) => new Set(prev).add(mainId));
      setActiveSubModule((prev) =>
        subModules.includes(prev) ? prev : subModules[0] || "",
      );
    },
    [visibleFaqData],
  );

  useEffect(() => {
    if (selectedGuide) return;
    const mains = Object.keys(visibleFaqData);
    if (!mains.length) {
      if (activeMainModule) {
        setActiveMainModule("");
        setActiveSubModule("");
        setExpandedNav(new Set());
      }
      return;
    }
    // Do not auto-pick / auto-expand the first topic — tree stays collapsed until the user opens one.
    if (activeMainModule && visibleFaqData[activeMainModule]) return;
    if (activeMainModule) {
      setActiveMainModule("");
      setActiveSubModule("");
    }
    setExpandedNav((prev) => {
      const next = new Set([...prev].filter((id) => visibleFaqData[id]));
      return next.size === prev.size && [...next].every((id) => prev.has(id))
        ? prev
        : next;
    });
  }, [visibleFaqData, activeMainModule, selectedGuide]);

  useEffect(() => {
    if (!selectedGuide) return;
    const topic = selectedGuide.moduleName || selectedGuide.moduleKey;
    if (!organizationCanSeeFaqTopic(userData, topic)) {
      setSelectedGuide(null);
    }
  }, [selectedGuide, userData]);

  useEffect(() => {
    const subs = Object.keys(visibleFaqData[activeMainModule] || {});
    if (!subs.length) {
      if (activeSubModule) setActiveSubModule("");
      return;
    }
    if (!subs.includes(activeSubModule)) {
      setActiveSubModule(subs[0]);
    }
  }, [visibleFaqData, activeMainModule, activeSubModule]);

  const allFAQs = useMemo(() => {
    const result = [];
    Object.entries(visibleFaqData).forEach(([mainModule, subModules]) => {
      Object.entries(subModules || {}).forEach(([subModule, faqs]) => {
        (faqs || []).forEach((faq) =>
          result.push({ ...faq, mainModule, subModule }),
        );
      });
    });
    return result;
  }, [visibleFaqData]);

  const moduleFAQs = useMemo(() => {
    if (!activeMainModule) return [];
    return allFAQs.filter((faq) => faq.mainModule === activeMainModule);
  }, [allFAQs, activeMainModule]);

  const keywords = useMemo(() => {
    const keywordSet = new Set();
    moduleFAQs.forEach((faq) => {
      const words =
        `${faq.question} ${faq.answer}`.toLowerCase().match(/\b\w{3,}\b/g) ||
        [];
      words.forEach((word) => keywordSet.add(word));
    });
    return Array.from(keywordSet).sort().slice(0, KEYWORD_LIMIT);
  }, [moduleFAQs]);

  const categories = useMemo(() => {
    return Object.entries(visibleFaqData)
      .map(([id, subModules]) => ({
        id,
        label: id,
        subItems: Object.keys(subModules || {}),
      }))
      .filter((cat) => cat.subItems.length > 0);
  }, [visibleFaqData]);

  const filteredFAQs = useMemo(() => {
    if (!searchQuery.trim()) {
      return visibleFaqData[activeMainModule]?.[activeSubModule] || [];
    }
    const query = searchQuery.toLowerCase();
    return moduleFAQs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query),
    );
  }, [
    searchQuery,
    activeMainModule,
    activeSubModule,
    visibleFaqData,
    moduleFAQs,
  ]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setExpandedItems(new Set());
    }
  }, [activeSubModule, activeMainModule, searchQuery]);

  useEffect(() => {
    if (!selectedVideo) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedVideo(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedVideo]);

  const categoryTitle = useMemo(() => {
    if (searchQuery.trim()) {
      return activeMainModule
        ? `Search results — ${activeMainModule}`
        : "Search results";
    }
    if (activeMainModule && activeSubModule)
      return `${activeMainModule} — ${activeSubModule}`;
    if (activeMainModule) return activeMainModule;
    return "FAQs";
  }, [searchQuery, activeMainModule, activeSubModule]);

  const filteredKeywords = useMemo(() => {
    if (!showSuggestions || !keywords.length) return [];
    const query = searchQuery.toLowerCase();
    return keywords
      .filter((keyword) => keyword.includes(query) && keyword !== query)
      .slice(0, 10);
  }, [keywords, searchQuery, showSuggestions]);

  useEffect(() => {
    setRecentItems(readFaqRecent());
    return subscribeFaqRecent(setRecentItems);
  }, []);

  const visibleRecentItems = useMemo(
    () =>
      recentItems
        .filter((item) => {
          const topic = item.mainModule || item.module;
          if (!topic) return true;
          return organizationCanSeeFaqTopic(userData, topic);
        })
        .slice(0, 8),
    [recentItems, userData],
  );

  useEffect(() => {
    if (!pendingTreeScroll) return;
    const main = String(pendingTreeScroll.main || "").trim();
    const sub = String(pendingTreeScroll.sub || "").trim();
    if (!main) {
      setPendingTreeScroll(null);
      return;
    }
    if (activeMainModule !== main) return;
    if (sub && activeSubModule !== sub) return;
    if (sub && !expandedNav.has(main)) return;

    const treeDomId = sub ? faqTreeLeafId(main, sub) : faqTreeModId(main);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollWithinContainer(document.getElementById(treeDomId), ".faq-nav");
        setPendingTreeScroll(null);
      });
    });
  }, [
    pendingTreeScroll,
    activeMainModule,
    activeSubModule,
    expandedNav,
  ]);

  useEffect(() => {
    if (!pendingExpandFaqId || selectedGuide) return;
    const needle = String(pendingExpandFaqId).trim();
    if (!needle) return;

    const index = filteredFAQs.findIndex((faq) => {
      const recentId = buildFaqRecentId(faq);
      const faqId = String(faq.faqId || "").trim();
      const question = String(faq.question || "").trim();
      return (
        recentId === needle ||
        (faqId && (faqId === needle || `faq:${faqId}` === needle)) ||
        (question && question === needle)
      );
    });
    if (index < 0) return;

    setExpandedItems(new Set([index]));
    setPendingExpandFaqId("");

    const faq = filteredFAQs[index];
    const itemDomId = `faq-item-${faq.faqId || index}`;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollWithinContainer(
          document.getElementById(itemDomId),
          ".faq-questions-list",
        );
      });
    });
  }, [pendingExpandFaqId, filteredFAQs, selectedGuide]);

  /** Open AQ Assist only when the organisation has access; otherwise show a helpful dialog. */
  const openAqAssist = useCallback(
    async (launchSession = null) => {
      const organizationId =
        userData?.OrganizationId || userData?.Organizationid || "";
      const staffId = userData?.StaffId || "";
      showLoader();
      try {
        const status = await fetchFaqAiStatus(organizationId, staffId);
        if (!status?.enabled) {
          setAssistUnavailableMessage(
            "AQ Assist is not enabled for your organisation yet.",
          );
          setAssistUnavailableOpen(true);
          return;
        }
        setAskLaunchSession(launchSession);
        setAskModalOpen(true);
      } catch (err) {
        setAssistUnavailableMessage(
          "AQ Assist is not available right now. You can request access below.",
        );
        setAssistUnavailableOpen(true);
      } finally {
        hideLoader();
      }
    },
    [userData, showLoader, hideLoader],
  );

  const handleAssistSupportSubmit = useCallback(
    async (requestNote) => {
      const note = String(requestNote || "").trim();
      if (!note || supportSubmitting) return;
      setSupportSubmitting(true);
      try {
        const orgName =
          userData?.OrganizationName ||
          userData?.OrgName ||
          userData?.organisationName ||
          "";
        const result = await submitFeedbackToClickUp({
          inquiryType: "Requesting a New Feature",
          module: "FAQ",
          taskName: "Support: Enable AQ Assist for organisation",
          description: [
            note,
            "",
            "---",
            "Context: User opened Help → FAQ → AQ Assist and the feature is not enabled for this organisation.",
            orgName ? `Organisation: ${orgName}` : null,
          ]
            .filter(Boolean)
            .join("\n"),
        });
        if (result.success) {
          showToast({
            title: "Support ticket created",
            message:
              "Your request has been sent to the AQ team. We will get back to you shortly.",
            type: "success",
            icon: "fas fa-check-circle",
            duration: 5000,
          });
          setAssistUnavailableOpen(false);
        } else {
          showToast({
            title: "Unable to create ticket",
            message:
              result.message ||
              result.error ||
              "We could not create your support ticket right now. Please try again.",
            type: "danger",
            icon: "fas fa-exclamation-circle",
            duration: 7000,
          });
        }
      } catch (error) {
        showToast({
          title: "Error",
          message:
            error?.message ||
            "An unexpected error occurred while creating your support ticket.",
          type: "danger",
          icon: "fas fa-exclamation-circle",
          duration: 7000,
        });
      } finally {
        setSupportSubmitting(false);
      }
    },
    [showToast, supportSubmitting, userData],
  );

  const openRecentItem = useCallback(
    (item) => {
      if (!item) return;
      if (item.type === "ask") {
        openAqAssist({
          chatSessionId: item.chatSessionId || "",
          module: item.module || item.mainModule || "",
          title: item.title || "",
          nonce: Date.now(),
        });
        return;
      }
      setSelectedGuide(null);
      setSearchQuery("");
      setShowSuggestions(false);
      setExpandedItems(new Set());
      if (item.mainModule) {
        selectFaqTopic(item.mainModule, item.subModule || "");
        setPendingTreeScroll({
          main: item.mainModule,
          sub: item.subModule || "",
        });
      }
      setPendingExpandFaqId(
        item.faqId || item.id || item.title || "",
      );
    },
    [selectFaqTopic, openAqAssist],
  );

  const handleAskModalClose = useCallback(() => {
    setAskModalOpen(false);
    setAskLaunchSession(null);
  }, []);

  const handleAskLaunchHandled = useCallback(() => {
    setAskLaunchSession(null);
  }, []);

  useEffect(() => {
    const staffId = userData?.StaffId || "";
    if (!staffId || hasCompletedFaqPageTour(staffId)) return undefined;
    if (askModalOpen) return undefined;
    const timer = window.setTimeout(() => setShowFaqTour(true), 900);
    return () => window.clearTimeout(timer);
  }, [userData?.StaffId, askModalOpen]);

  const faqTourSteps = useMemo(
    () =>
      buildFaqPageTourSteps({
        hasTopics: categories.length > 0,
        hasQuestions: filteredFAQs.length > 0 && !selectedGuide,
        hasRecent: visibleRecentItems.length > 0,
        hasUserGuides: visibleUserGuides.length > 0,
      }),
    [
      categories.length,
      filteredFAQs.length,
      selectedGuide,
      visibleRecentItems.length,
      visibleUserGuides.length,
    ],
  );

  const trackFaqOpen = useCallback((faq, mainModule, subModule) => {
    if (!faq?.question) return;
    pushFaqRecent({
      id: buildFaqRecentId(faq),
      type: "faq",
      title: faq.question,
      faqId: faq.faqId || "",
      mainModule: mainModule || faq.mainModule || "",
      subModule: subModule || faq.subModule || "",
      module: mainModule || faq.mainModule || "",
    });
  }, []);

  return (
    <div className="faq-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "FAQ" },
        ]}
        trailingExtras={
          <span className="faq-ask-open-wrap">
            <button
              type="button"
              className="isf-tour-help-btn"
              data-tour="faq-page-guide"
              title="Show FAQ guide"
              onClick={() => setShowFaqTour(true)}
            >
              <i className="fas fa-circle-question" aria-hidden="true" />
              Guide
            </button>
            <span className="faq-ask-open-pointer" aria-hidden="true">
              <i className="fas fa-arrow-right" />
            </span>
            <button
              type="button"
              className="faq-ask-open-btn"
              data-tour="faq-aq-assist"
              onClick={() => openAqAssist(null)}
              title="AQ Assist"
              aria-label="Open AQ Assist"
            >
              <FaqAssistMark />
              AQ Assist
              <FaqAssistAiIcon className="faq-ask-open-btn__ai" />
            </button>
          </span>
        }
      />
      <div className="faq-main">
        <aside className="faq-sidebar" data-tour="faq-topics">
          <div className="faq-sidebar-head">
            <span className="faq-sidebar-title">Topics</span>
          </div>
          <nav className="faq-nav faq-tree" aria-label="FAQ sections">
            {categories.map((category) => {
              const hasSubItems = category.subItems?.length > 0;
              const isBranchOpen = hasSubItems && expandedNav.has(category.id);
              const isModuleActive =
                !selectedGuide && activeMainModule === category.id;
              return (
                <div
                  key={category.id}
                  id={faqTreeModId(category.id)}
                  className={`faq-tree-node faq-tree-mod--${faqModuleAccent(category.id)}`}
                >
                  <div
                    className={`faq-tree-row${isModuleActive ? " is-current" : ""}${isBranchOpen ? " is-open" : ""}`}
                  >
                    {hasSubItems ? (
                      <button
                        type="button"
                        className="faq-tree-twistie"
                        aria-label={
                          isBranchOpen
                            ? `Collapse ${category.label}`
                            : `Expand ${category.label}`
                        }
                        aria-expanded={isBranchOpen}
                        onClick={() => {
                          setExpandedNav((prev) => {
                            const next = new Set(prev);
                            if (next.has(category.id)) next.delete(category.id);
                            else next.add(category.id);
                            return next;
                          });
                        }}
                      >
                        <i
                          className={`fas fa-chevron-${isBranchOpen ? "down" : "right"}`}
                          aria-hidden="true"
                        />
                      </button>
                    ) : (
                      <span className="faq-tree-twistie is-leaf" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      className="faq-tree-label"
                      onClick={() => selectFaqTopic(category.id)}
                    >
                      <span className="faq-tree-icon-chip" aria-hidden="true">
                        {faqUsesAqLogo(category.id) ? (
                          <img
                            src="/logo.png"
                            alt=""
                            className="faq-tree-aq-logo"
                          />
                        ) : (
                          <i
                            className={`faq-tree-module-icon ${faqModuleIcon(category.id)}`}
                          />
                        )}
                      </span>
                      <span className="faq-tree-label-text">{category.label}</span>
                    </button>
                  </div>
                  {hasSubItems && isBranchOpen ? (
                    <div className="faq-tree-children">
                      {category.subItems.map((subItem) => {
                        const isLeafActive =
                          isModuleActive && activeSubModule === subItem;
                        return (
                          <button
                            key={subItem}
                            id={faqTreeLeafId(category.id, subItem)}
                            type="button"
                            className={`faq-tree-leaf${isLeafActive ? " is-active" : ""}`}
                            onClick={() => selectFaqTopic(category.id, subItem)}
                          >
                            <span className="faq-tree-leaf-dot" aria-hidden="true" />
                            <span className="faq-tree-leaf-text">{subItem}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {visibleUserGuides.length > 0 ? (
              <div
                className="faq-tree-node faq-tree-node--resources faq-tree-mod--guide"
                data-tour="faq-resources"
              >
                <div className="faq-tree-section">Resources</div>
                <div
                  className={`faq-tree-row${selectedGuide ? " is-current" : ""}${expandedUserGuide ? " is-open" : ""}`}
                >
                  <button
                    type="button"
                    className="faq-tree-twistie"
                    aria-expanded={expandedUserGuide}
                    aria-label={
                      expandedUserGuide
                        ? "Collapse user guides"
                        : "Expand user guides"
                    }
                    onClick={() => setExpandedUserGuide((open) => !open)}
                  >
                    <i
                      className={`fas fa-chevron-${expandedUserGuide ? "down" : "right"}`}
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    type="button"
                    className="faq-tree-label"
                    onClick={() => setExpandedUserGuide((open) => !open)}
                  >
                    <span className="faq-tree-icon-chip" aria-hidden="true">
                      <i
                        className={`faq-tree-module-icon ${faqModuleIcon("User Guide")}`}
                      />
                    </span>
                    <span className="faq-tree-label-text">User Guide</span>
                  </button>
                </div>
                {expandedUserGuide ? (
                  <div className="faq-tree-children">
                    {visibleUserGuides.map((guide) => {
                      const guideKey = guide.moduleKey || guide.moduleName;
                      const isGuideActive =
                        !!selectedGuide &&
                        (selectedGuide.moduleKey === guideKey ||
                          selectedGuide.moduleName === guide.moduleName);
                      return (
                        <button
                          key={guideKey}
                          type="button"
                          className={`faq-tree-leaf${isGuideActive ? " is-active" : ""}${guideLoading ? " is-loading" : ""}`}
                          disabled={guideLoading}
                          onClick={() => openUserGuide(guide)}
                        >
                          <span className="faq-tree-leaf-dot" aria-hidden="true" />
                          <span className="faq-tree-leaf-text">
                            {guide.moduleName || guide.moduleKey}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>
        </aside>

        <section className={`faq-content${selectedGuide ? " is-guide" : ""}`}>
          {!selectedGuide ? (
          <div className="faq-toolbar">
            <div className="faq-toolbar-label">
              <h2 className="faq-questions-title" title={categoryTitle}>
                {categoryTitle}
              </h2>
              <span className="faq-questions-count">
                {filteredFAQs.length}{" "}
                {filteredFAQs.length === 1 ? "question" : "questions"}
              </span>
              {loadError ? (
                <p className="faq-load-hint">{loadError}</p>
              ) : null}
            </div>

            <div className="faq-search-section" data-tour="faq-search">
              <div className="faq-search-wrapper">
                <i className="fas fa-search faq-search-icon"></i>
                <input
                  type="text"
                  className={`faq-search-input${searchQuery ? " has-clear" : ""}`}
                  placeholder={
                    activeMainModule
                      ? `Search in ${activeMainModule}…`
                      : "Search questions or keywords…"
                  }
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    setShowSuggestions(value.length > 0);
                  }}
                  onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), SUGGESTION_DELAY)
                  }
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="faq-search-clear"
                    aria-label="Clear search"
                    title="Clear search"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSearchQuery("");
                      setShowSuggestions(false);
                      setExpandedItems(new Set());
                    }}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                ) : null}
                {showSuggestions && filteredKeywords.length > 0 && (
                  <div className="faq-keyword-suggestions">
                    <div className="faq-keyword-label">Keywords</div>
                    <div className="faq-keywords-list">
                      {filteredKeywords.map((keyword) => (
                        <button
                          key={keyword}
                          className="faq-keyword-tag"
                          onClick={() => {
                            setSearchQuery(keyword);
                            setShowSuggestions(false);
                          }}
                        >
                          {keyword}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          ) : null}

          {selectedGuide ? (
            <div className="faq-guide-panel">
              <div className="faq-guide-panel-toolbar">
                <span className="faq-guide-panel-toolbar-label">
                  {(() => {
                    const name = String(selectedGuide.moduleName || "").trim();
                    if (!name) return "User Guide";
                    return /user\s*guide$/i.test(name)
                      ? name
                      : `${name} User Guide`;
                  })()}
                </span>
                <div className="faq-guide-panel-actions">
                  <button
                    type="button"
                    className="faq-guide-panel-action"
                    title="Open in new tab"
                    aria-label="Open user guide in new tab"
                    onClick={() =>
                      window.open(
                        selectedGuide.fileUrl,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <i className="fas fa-external-link-alt" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="faq-guide-panel-action"
                    title="Back to FAQs"
                    aria-label="Close user guide"
                    onClick={() => {
                      closeUserGuide();
                      const first = categories[0]?.id;
                      if (first) selectFaqTopic(first);
                    }}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="faq-guide-panel-body">
                {guideLoading || !selectedGuide.fileUrl ? (
                  <div className="faq-guide-panel-loading">Loading guide…</div>
                ) : (
                  <iframe
                    className="faq-pdf-iframe"
                    src={selectedGuide.fileUrl}
                    title={`${selectedGuide.moduleName} User Guide`}
                  />
                )}
              </div>
            </div>
          ) : null}

          {!selectedGuide ? (
          <div className="faq-questions-section" data-tour="faq-questions">
            <div className="faq-questions-list" role="list">
              {filteredFAQs.map((faq, index) => {
                const isExpanded = expandedItems.has(index);
                const itemId = faq.faqId || `faq-${index}`;
                const domId = `faq-item-${faq.faqId || index}`;
                return (
                  <div
                    key={itemId}
                    id={domId}
                    className={`faq-item ${isExpanded ? "expanded" : ""}`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="faq-item-header"
                      aria-expanded={isExpanded}
                      aria-controls={`${itemId}-answer`}
                      onClick={() => {
                        setExpandedItems((prev) => {
                          if (prev.has(index)) return new Set();
                          trackFaqOpen(
                            faq,
                            faq.mainModule || activeMainModule,
                            faq.subModule || activeSubModule,
                          );
                          return new Set([index]);
                        });
                      }}
                    >
                      <span className="faq-item-index" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="faq-item-header-content">
                        <span className="faq-item-question">{faq.question}</span>
                        {searchQuery.trim() &&
                          faq.mainModule &&
                          faq.subModule && (
                            <span className="faq-item-badge">
                              {faq.subModule}
                            </span>
                          )}
                      </div>
                      <span className="faq-item-icon" aria-hidden="true">
                        <i
                          className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                        />
                      </span>
                    </button>
                    <div
                      id={`${itemId}-answer`}
                      className={`faq-item-content ${isExpanded ? "expanded" : ""}`}
                      hidden={!isExpanded}
                    >
                      {isExpanded ? (
                        <FaqAnswerContent
                          faq={faq}
                          onPlayVideo={setSelectedVideo}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredFAQs.length === 0 && (
              <div className="faq-no-results">
                <div className="faq-no-results-icon" aria-hidden="true">
                  <i className="fas fa-search" />
                </div>
                <p>
                  {searchQuery.trim()
                    ? activeMainModule
                      ? `No questions found in ${activeMainModule} for this search.`
                      : "No questions found matching your search."
                    : categories.length === 0
                      ? "No FAQs available."
                      : activeMainModule
                        ? "No questions found for this section."
                        : "Select a topic from the list to view questions."}
                </p>
                {searchQuery.trim() ? (
                  <button
                    type="button"
                    className="faq-no-results-clear"
                    onClick={() => {
                      setSearchQuery("");
                      setShowSuggestions(false);
                      setExpandedItems(new Set());
                    }}
                  >
                    Clear search
                  </button>
                ) : null}
              </div>
            )}
          </div>
          ) : null}
        </section>

        <aside className="faq-right-rail" aria-label="Recent and user guides">
          <div className="faq-right-rail-panel">
            <div className="faq-right-rail-block" data-tour="faq-recent">
              <div className="faq-right-rail-head">
                <span className="faq-right-rail-title">Recent</span>
              </div>
              <div className="faq-right-rail-body">
                {visibleRecentItems.length === 0 ? (
                  <p className="faq-right-rail-empty">
                    Open an FAQ or ask AQ — your recent items appear here.
                  </p>
                ) : (
                  <ul className="faq-right-rail-list">
                    {visibleRecentItems.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className="faq-right-rail-link"
                          onClick={() => openRecentItem(item)}
                          title={item.title}
                        >
                          <span
                            className={`faq-right-rail-link-icon faq-right-rail-link-icon--${item.type}`}
                            aria-hidden="true"
                          >
                            <i
                              className={`fas ${
                                item.type === "ask"
                                  ? "fa-comments"
                                  : "fa-question"
                              }`}
                            />
                          </span>
                          <span className="faq-right-rail-link-body">
                            <span className="faq-right-rail-link-title">
                              {item.title}
                            </span>
                            <span className="faq-right-rail-link-meta">
                              {item.type === "ask" ? "AQ Assist" : "FAQ"}
                              {item.mainModule || item.module
                                ? ` · ${item.mainModule || item.module}`
                                : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="faq-right-rail-block" data-tour="faq-user-guides">
              <div className="faq-right-rail-head">
                <span className="faq-right-rail-title">User guides</span>
              </div>
              <div className="faq-right-rail-body">
                {visibleUserGuides.length === 0 ? (
                  <p className="faq-right-rail-empty">
                    No user guides available for your organisation.
                  </p>
                ) : (
                  <ul className="faq-right-rail-list">
                    {visibleUserGuides.map((guide) => {
                      const guideKey = guide.moduleKey || guide.moduleName;
                      const isGuideActive =
                        !!selectedGuide &&
                        (selectedGuide.moduleKey === guideKey ||
                          selectedGuide.moduleName === guide.moduleName);
                      return (
                        <li key={guideKey}>
                          <button
                            type="button"
                            className={`faq-right-rail-link${isGuideActive ? " is-active" : ""}`}
                            disabled={guideLoading}
                            onClick={() => openUserGuide(guide)}
                            title={`${guide.moduleName || guide.moduleKey} User Guide`}
                          >
                            <span
                              className="faq-right-rail-link-icon faq-right-rail-link-icon--guide"
                              aria-hidden="true"
                            >
                              <i className="fas fa-book" />
                            </span>
                          <span className="faq-right-rail-link-body">
                            <span className="faq-right-rail-link-title">
                              {guide.moduleName || guide.moduleKey} User Guide
                            </span>
                          </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {selectedVideo && (
        <div
          className="faq-pdf-modal faq-video-modal"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="faq-pdf-modal-content faq-video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="faq-pdf-modal-header">
              <h3 className="faq-pdf-modal-title">
                {selectedVideo.title || "Guide video"}
              </h3>
              <div className="faq-pdf-modal-actions">
                <button
                  type="button"
                  className="faq-pdf-download-btn"
                  onClick={() =>
                    window.open(
                      selectedVideo.shareUrl || selectedVideo.embedUrl,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <i className="fas fa-external-link-alt" /> Open in new tab
                </button>
                <button
                  type="button"
                  className="faq-pdf-close-btn"
                  onClick={() => setSelectedVideo(null)}
                  aria-label="Close video"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
            <div className="faq-pdf-modal-body faq-video-modal-body">
              <div className="faq-video-embed-wrap">
                <iframe
                  className="faq-video-iframe"
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title || "Guide video"}
                  frameBorder="0"
                  referrerPolicy="unsafe-url"
                  allowFullScreen
                  allow="clipboard-write"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <FaqAskModal
        open={askModalOpen}
        onClose={handleAskModalClose}
        fallbackModules={categories.map((category) => category.id)}
        launchSession={askLaunchSession}
        onLaunchSessionHandled={handleAskLaunchHandled}
      />

      <FaqAssistUnavailableModal
        open={assistUnavailableOpen}
        message={assistUnavailableMessage}
        submitting={supportSubmitting}
        onClose={() => {
          if (!supportSubmitting) setAssistUnavailableOpen(false);
        }}
        onSubmitRequest={handleAssistSupportSubmit}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <IsfPageTour
        open={showFaqTour && !askModalOpen}
        steps={faqTourSteps}
        staffId={userData?.StaffId || ""}
        markTourCompleted={markFaqPageTourCompleted}
        onClose={() => setShowFaqTour(false)}
      />
    </div>
  );
};

export default FAQ;
